import ApiRequest, { ApiError, is200Status, isStatusError } from './apiRequest';

export class LoginError extends ApiError {
  get message() {
    return Object.values(this.error.response.data).join('. ');
  }
}

export default class UsersApi extends ApiRequest {
  resend_email_token = '';

  makeRequest(method, url, data, responseType = 'json') {
    return super.makeRequest(method, url, data, responseType);
  }

  register(data) {
    return this.post('users/', data)
      .then(
        is200Status(response => {
          this.resend_email_token = response.data.user.resend_email_token;

          return response.data;
        }),
      )
      .catch(error => {
        throw error.response.data;
      });
  }

  checkEmailConfirmationStatus(email) {
    return this.get(`user/check_email_confirmation?email=${email}`).then(is200Status(response => response.data));
  }

  changeVerificationEmail(email) {
    return this.post('users/confirmation', {
      user: {
        email,
        resend_email_token: this.resend_email_token,
      },
    }).then(is200Status());
  }

  login(data) {
    return this.post('users/login', data)
      .then(
        is200Status(response => {
          const { authentication_token, first_name, last_name } = response.data.user;

          this.setToken(authentication_token);
          localStorage.setItem('token', authentication_token);
          localStorage.setItem('userName', `${first_name} ${last_name}`);

          return response.data.user;
        }),
      )
      .catch(
        isStatusError(401)(error => {
          throw new LoginError(error);
        }),
      );
  }

  logout() {
    return this.delete('users/logout').then(
      is200Status(() => {
        this.setToken('');
        localStorage.removeItem('token');
        localStorage.removeItem('userName');
      }),
    );
  }

  getResetPasswordEmail(data) {
    return this.post('users/password', data).then(
      is200Status(response => {
        return response.data;
      }),
    );
  }

  resetPassword(data) {
    return this.patch('users/password', data).then(
      is200Status(response => {
        return response.data;
      }),
    );
  }

  verifyEmail(searchTerm) {
    return this.get(`users/confirmation?confirmation_token=${searchTerm}`).then(
      is200Status(response => {
        this.setToken(response.data.user.authentication_token);
        localStorage.setItem('token', response.data.user.authentication_token);

        return response.data.user;
      }),
    );
  }

  checkEmail(email) {
    return this.get(`user/check_email?email=${encodeURIComponent(email)}`)
      .then(is200Status(() => false))
      .catch(isStatusError(409)(() => true));
  }

  getUserPersonalData() {
    return this.get('user').then(is200Status(response => response.data.user));
  }

  getUserCreditCardData() {
    return this.get('user/card').then(is200Status(response => response.data.card));
  }

  updateUserData(data) {
    return this.patch('user', data).then(is200Status(response => response.data));
  }

  updateCardData(data) {
    return this.patch('user/card', data).then(is200Status(response => response.data));
  }

  getSchedule() {
    return this.get('user/schedule').then(is200Status(response => response.data.schedule));
  }

  updateSchedule(data) {
    return this.patch('user/schedule', data).then(is200Status(response => response.data));
  }

  getExercises(filter) {
    const url = filter ? `user/dashboard/review/${filter}` : 'user/dashboard';

    return this.get(url).then(is200Status(response => response.data.exercises));
  }

  getCurrentWeekExercises(week, target) {
    if (target === 'exercises') {
      return this.get(`user/weeks/${week}/exercises`).then(is200Status(response => response.data.week_plan));
    }

    // bottom code block was refactored to inform server about current exercise "viewed" status:
    // user is shown PDF with static link (week_plan.pdf_original_url) after a server being pinged
    return this.get(`user/weeks/${week}/pdf`, {}, 'blob').then(response => response.status === 200);
  }

  getExerciseReview(number) {
    return this.get(`user/exercises/${number}/review`).then(is200Status(response => response.data.review));
  }

  reviewExercise(number, value, message = '') {
    const review = message ? { value, message } : { value };

    return this.patch(`user/exercises/${number}/review`, { review }).then(
      is200Status(response => response.data.review.value),
    );
  }

  reviewExercises(week, reviews) {
    return this.post(`user/weeks/${week}/review`, { week_plan: { exercises: reviews } });
  }

  makeExerciseCurrent(number) {
    return this.post('user/weeks/make_current', { exercise: { number } }).then(
      is200Status(response => response.data.week_plan.exercises),
    );
  }

  downloadExercise(id = '') {
    // "Download All" option should be double checked (if it is still needed)
    const url = id ? `user/exercises/${id}/pdf` : 'user/download_all';

    // bottom code block was refactored to inform server about current exercise "viewed" status:
    // user is shown PDF with static link (pdf_original_url) after a server being pinged (see getCurrentWeekExercises method)
    return this.get(url, {}, 'blob').then(response => response.status === 200);
  }

  subscribe() {
    return this.post('user/subscribe').then(is200Status(response => response.data));
  }

  unsubscribe() {
    return this.post('user/unsubscribe').then(is200Status(response => response.data));
  }

  suspendSubscription() {
    return this.post('user/suspend_subscription').then(is200Status(response => response.data));
  }

  sendSupportMessage(data) {
    return this.post('support/send_message', data).then(is200Status(response => response.data));
  }
}



// WEBPACK FOOTER //
// ./src/api/usersApi.js